import { axiosInstance as api } from "./request";
import { v4 as uuid } from "uuid";
import { UploadLifetime } from "./UploadLifetime";
import { retryOperation } from "./retryOperation";

interface IOptions {
  requestKey?: string;
  chunkSize?: number;
  threadsQuantity?: number;
  streamId: string;
  file: any;
  fileName: string;
  contentType: string;
}

// original source: https://github.com/pilovm/multithreaded-uploader/blob/master/frontend/uploader.js
export class Uploader {
  readonly requestKey: string;
  readonly lifetime: UploadLifetime;
  intentId: string | null;
  private completing = false;
  chunkSize: number;
  threadsQuantity: number;
  streamId: string;
  file: any;
  fileName: string;
  contentType: string;
  aborted: boolean;
  uploadedSize: number;
  progressCache: any;
  activeConnections: any;
  parts: any[];
  uploadedParts: any[];
  fileId: string | null;
  fileKey: string | null;
  onProgressFn: (data: {
    percentage: number;
    sent: number;
    total: number;
  }) => any;
  onErrorFn: (data: any) => any;
  onCompleteFn: (data: { Key: string; url: string }) => any;

  constructor(options: IOptions) {
    // this must be bigger than or equal to 5MB,
    // otherwise AWS will respond with:
    // "Your proposed upload is smaller than the minimum allowed size"
    this.chunkSize = options.chunkSize || 1024 * 1024 * 10;
    // number of parallel uploads
    this.threadsQuantity = Math.min(options.threadsQuantity || 2, 15);
    this.streamId = options.streamId;
    this.file = options.file;
    this.fileName = options.fileName;
    this.contentType = options.contentType;
    this.requestKey = options.requestKey ?? uuid();
    this.lifetime = new UploadLifetime({ requestKey: this.requestKey, streamId: options.streamId, name: options.fileName, size: options.file.size, contentType: options.contentType, state: 'active' });
    this.intentId = this.lifetime.record.intentId ?? null;
    this.lifetime.onExpired = error => {
      this.aborted = true;
      Object.values(this.activeConnections).forEach((xhr: any) => xhr.abort());
      this.onErrorFn(error);
    };
    this.aborted = this.lifetime.record.state === 'cancelled';
    this.uploadedSize = 0;
    this.progressCache = {};
    this.activeConnections = {};
    this.parts = [];
    this.uploadedParts = [];
    this.fileId = null;
    this.fileKey = null;
    this.onProgressFn = () => {};
    this.onErrorFn = () => {};
    this.onCompleteFn = () => {};
  }

  // starting the multipart upload request
  start() {
    this.initialize();
  }

  async initialize() {
    try {
      // initializing the multipart request

      const videoInitializationUploadInput = {
        requestKey: this.requestKey,
        fileName: this.fileName,
        contentType: this.contentType,
        // Reserved against the stream's storage cap (STORAGE_OVER_CAP → 422).
        contentLength: this.file.size,
      };

      const initializeReponse = await api.request({
        url: `/streams/${this.streamId}/visual-media/multipart/init`,
        method: "POST",
        data: videoInitializationUploadInput,
      });

      const AWSFileDataOutput = initializeReponse.data;

      this.fileId = AWSFileDataOutput.fileId;
      this.fileKey = AWSFileDataOutput.fileKey;
      this.intentId = AWSFileDataOutput.intentId;
      this.lifetime.bind(this.intentId!, this.fileKey!);
      if (this.aborted) { await this.lifetime.cancel(); return; }

      // retrieving the pre-signed URLs
      const numberOfparts = Math.ceil(this.file.size / this.chunkSize);

      const AWSMultipartFileDataInput = {
        fileId: this.fileId,
        fileKey: this.fileKey,
        parts: numberOfparts,
      };

      const urlsResponse = await api.request({
        url: `/streams/${this.streamId}/visual-media/multipart/generate-urls`,
        method: "POST",
        data: AWSMultipartFileDataInput,
      });

      const newParts = urlsResponse.data.parts;
      this.parts.push(...newParts);

      this.sendNext();
    } catch (error) {
      await this.complete(error);
    }
  }

  sendNext() {
    if (this.aborted || this.completing) return;
    const activeConnections = Object.keys(this.activeConnections).length;

    if (activeConnections >= this.threadsQuantity) {
      return;
    }

    if (!this.parts.length) {
      if (!activeConnections) {
        this.complete(null);
      }

      return;
    }

    const part = this.parts.pop();
    if (this.file && part) {
      const sentSize = (part.PartNumber - 1) * this.chunkSize;
      const chunk = this.file.slice(sentSize, sentSize + this.chunkSize);

      const sendChunkStarted = () => {
        this.sendNext();
      };

      this.sendChunk(chunk, part, sendChunkStarted)
        .then(() => {
          this.sendNext();
        })
        .catch((error) => {
          this.parts.push(part);

          this.complete(error);
        });
    }
  }

  // terminating the multipart upload request on success or failure
  async complete(error: any) {
    if (this.aborted) return;
    if (error) {
      this.fail(error);
      return;
    }
    if (this.completing) return;
    this.completing = true;
    try {
      await this.sendCompleteRequest();
    } catch (error) {
      this.fail(error);
    }
  }

  /** UC-5.1: one failed part ends the transfer. Stop every thread and release the reserved quota now. */
  private fail(error: any) {
    if (this.aborted) return;
    this.aborted = true;
    Object.values(this.activeConnections).forEach((xhr: any) => xhr.abort());
    this.activeConnections = {};
    void this.lifetime.cancel();
    this.onErrorFn(error);
  }

  // finalizing the multipart upload request on success by calling
  // the finalization API
  async sendCompleteRequest() {
    if (!this.aborted && this.fileId && this.fileKey) {
      const videoFinalizationMultiPartInput = {
        fileId: this.fileId,
        fileKey: this.fileKey,
        parts: this.uploadedParts,
      };

      const response = await api.request({
        url: `/streams/${this.streamId}/visual-media/multipart/finalize`,
        method: "POST",
        data: videoFinalizationMultiPartInput,
      });

      if (this.aborted) return;
      await this.onCompleteFn(response.data);
      this.lifetime.finish();
    }
  }

  sendChunk(chunk: any, part: number, sendChunkStarted: any) {
    return new Promise((resolve, reject) => {
      retryOperation(
        async () => {
          try {
            if (this.aborted) throw new Error("Upload cancelled");
            const status = await this.upload(chunk, part, sendChunkStarted);
            if (status !== 200) {
              throw new Error("Failed chunk upload");
            }
          } catch (err) {
            throw err;
          }
        },
        1000,
        10
      )
        .then(() => resolve(true))
        .catch((error: any) =>
          reject(error || new Error("Something went wrong"))
        );
    });
  }

  // calculating the current progress of the multipart upload request
  handleProgress(part: any, event: any) {
    if (this.file) {
      if (
        event.type === "progress" ||
        event.type === "error" ||
        event.type === "abort"
      ) {
        this.progressCache[part] = event.loaded;
      }

      if (event.type === "uploaded") {
        this.uploadedSize += this.progressCache[part] || 0;
        delete this.progressCache[part];
      }

      const inProgress = Object.keys(this.progressCache)
        .map(Number)
        .reduce((memo, id) => (memo += this.progressCache[id]), 0);

      const sent = Math.min(this.uploadedSize + inProgress, this.file.size);

      const total = this.file.size;

      const percentage = Math.round((sent / total) * 100);

      this.onProgressFn({
        sent: sent,
        total: total,
        percentage: percentage,
      });
    }
  }

  // uploading a part through its pre-signed URL
  upload(file: File, part: any, sendChunkStarted: any) {
    if (this.aborted) return Promise.reject(new Error("Upload cancelled"));
    // uploading each part with its pre-signed URL
    return new Promise((resolve, reject) => {
      if (this.fileId && this.fileKey) {
        // - 1 because PartNumber is an index starting from 1 and not 0
        const xhr = (this.activeConnections[part.PartNumber - 1] =
          new XMLHttpRequest());

        sendChunkStarted();

        const progressListener = this.handleProgress.bind(
          this,
          part.PartNumber - 1
        );

        xhr.upload.addEventListener("progress", progressListener);

        xhr.addEventListener("error", progressListener);
        xhr.addEventListener("abort", progressListener);
        xhr.addEventListener("loadend", progressListener);

        xhr.open("PUT", part.signedUrl);

        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4 && xhr.status === 200) {
            // retrieving the ETag parameter from the HTTP headers
            const ETag = xhr.getResponseHeader("ETag");

            if (ETag) {
              const uploadedPart = {
                PartNumber: part.PartNumber,
                // removing the " enclosing carachters from
                // the raw ETag
                ETag: ETag.replace(/\"/g, ""),
              };

              this.uploadedParts.push(uploadedPart);

              delete this.activeConnections[part.PartNumber - 1];
              resolve(xhr.status);
            }
          } else if (xhr.status >= 400) {
            delete this.activeConnections[part.PartNumber - 1];
            reject(new Error("Bad status code"));
          }
        };

        xhr.onerror = (error) => {
          delete this.activeConnections[part.PartNumber - 1];
          reject(error);
        };

        xhr.onabort = () => {
          delete this.activeConnections[part.PartNumber - 1];
          reject(new Error("Upload canceled by user"));
        };

        xhr.send(file);
      }
    });
  }

  onProgress(
    onProgress: (data: {
      percentage: number;
      sent: number;
      total: number;
    }) => void
  ) {
    this.onProgressFn = onProgress;
    return this;
  }

  onError(onError: any) {
    this.onErrorFn = onError;
    return this;
  }

  onComplete(onComplete: (data: { Key: string; url: string }) => void) {
    this.onCompleteFn = onComplete;
    return this;
  }

  async abort() {
    this.aborted = true;
    Object.values(this.activeConnections).forEach((xhr: any) => xhr.abort());
    return this.lifetime.cancel();
  }
}
